'use client';

import React, { useEffect, useState } from 'react';
import { RoundedSectionCard } from '@/components/common/RoundedSectionCard';
import { Document, DocumentCategory } from '@/actions/dtos/document';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFolderOpen, faFolderClosed, faChevronRight, faChevronDown } from '@fortawesome/pro-duotone-svg-icons';
import { getIconByDocType, formatFileSize, getFileContentTitle } from '@/utils/fileUtils';
import { event } from '@/utils/googleAnalytics';
import Link from 'next/link';
import { useHasTenderAccess } from '@/hooks/useHasTenderAccess';
import { useAuthenticated } from '@/hooks/useAuthenticated';
import TenderAccessButton from '../access/TenderAccessButton';
import { groupDocumentsIntoFolders } from '@/utils/documents';
import { FolderDocuments } from '@/types/documents';
import useWindowWidth from '@/hooks/useWindowWidth';
import { useDispatch } from 'react-redux';
import { openModal, FeatureType } from '@/slices/features/modalSlice';

interface TenderDocumentsSectionProps {
  tenderId: string;
  documents: Document[];
  isActiveTender: boolean;
}

const MAX_TEXT_LENGTH_LARGE_SCREEN = 85;
const MAX_TEXT_LENGTH_SMALL_SCREEN = 70;
const LARGE_SCREEN_BREAKPOINT = 1536;

const DocumentSection: React.FC<TenderDocumentsSectionProps> = ({ documents, isActiveTender, tenderId }) => {
  const { isAuthenticated, isLoading: isAuthenticationLoading } = useAuthenticated();
  const { hasAccessPermission, isLoading: isTenderAccessLoading } = useHasTenderAccess(tenderId);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showAccessButton, setShowAccessButton] = useState(isActiveTender && !isLoading);
  const [openedFolders, setOpenedFolders] = useState<Set<string>>(new Set());
  const { rootDocuments, rootFolders } = groupDocumentsIntoFolders(documents);
  const windowWidth = useWindowWidth();
  const dispatch = useDispatch();

  useEffect(() => {
    setShowAccessButton(isActiveTender && !isLoading);
  }, [isActiveTender, isLoading]);

  useEffect(() => {
    setIsLoading(isTenderAccessLoading || isAuthenticationLoading);
  }, [isAuthenticationLoading, isTenderAccessLoading]);

  useEffect(() => {
    const allFolderNames = getAllFolderNames(rootFolders);
    setOpenedFolders(allFolderNames);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getAllFolderNames = (folders: FolderDocuments[]): Set<string> => {
    const names: Set<string> = new Set<string>();

    folders.forEach((folder) => {
      names.add(folder.folderName);
      if (folder.subfolders && folder.subfolders.length > 0) {
        const subNames = getAllFolderNames(folder.subfolders);
        subNames.forEach((name) => names.add(name));
      }
    });

    return names;
  };

  const trimTitle = (text: string) => {
    const isLargeScreen = windowWidth > LARGE_SCREEN_BREAKPOINT;
    const maxLength = isLargeScreen ? MAX_TEXT_LENGTH_LARGE_SCREEN : MAX_TEXT_LENGTH_SMALL_SCREEN;
    if (text.length <= maxLength) {
      return text;
    }
    return text.slice(0, maxLength) + '…';
  };

  const renderDocumentLink = (documentName: string) => {
    const handleClick = () => {
      dispatch(openModal(FeatureType.TENDER_ACCESS));
    };
    if (hasAccessPermission) {
      return (
        <Link
          href={`${window.location.pathname}/quelle?tenderId=${tenderId}`}
          className="flex items-center"
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => {
            event({
              action: 'click',
              category: 'outbound',
              label: 'External link clicked',
              value: 'Document Link',
            });
          }}
        >
          {documentName}
        </Link>
      );
    } else {
      return (
        <div onClick={handleClick} className="cursor-pointer">
          {documentName}
        </div>
      );
    }
  };

  const toggleFolder = (folderPath: string) => {
    setOpenedFolders((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(folderPath)) {
        newSet.delete(folderPath);
      } else {
        newSet.add(folderPath);
      }
      return newSet;
    });
  };

  const renderFoldersWithDocuments = (folders: FolderDocuments[], nestedLevel = 0): React.ReactNode => {
    return (
      <>
        {folders.map((folder) => {
          const isOpenedFolder = openedFolders.has(folder.folderName);
          const nestedFilesQuantity = folder.documents?.length + folder.subfolders.length;
          return (
            <React.Fragment key={folder.folderName}>
              <tr key={folder.folderName} className="folder-row border-b border-[#939BA4]">
                <td className="relative flex w-12 py-2 font-light">
                  <div
                    onClick={() => toggleFolder(folder.folderName)}
                    className="absolute z-10 flex  h-7 w-7 items-center gap-2"
                    style={{ left: `${Math.min(nestedLevel * 28, 500)}px` }}
                  >
                    <div className="w-8">
                      <FontAwesomeIcon
                        icon={isOpenedFolder ? faChevronDown : faChevronRight}
                        className="fa-fw h-6 w-7 cursor-pointer"
                      />
                    </div>
                    <FontAwesomeIcon icon={faFolderClosed} className="fa-fw h-6 w-7" />
                  </div>

                  <span
                    className="absolute whitespace-nowrap"
                    title={folder.folderName}
                    style={{ left: `${Math.min(nestedLevel * 28 + 64, 500)}px` }}
                  >
                    {trimTitle(folder.folderName)}
                  </span>
                </td>
                <td />
                <td />
                <td />
                <td className="py-2 pl-5 pr-2 text-right font-light text-primary">
                  {nestedFilesQuantity} Datei{nestedFilesQuantity > 1 ? 'en' : ''}
                </td>
              </tr>
              {isOpenedFolder && folder.subfolders && renderFoldersWithDocuments(folder.subfolders, nestedLevel + 1)}
              {isOpenedFolder && renderDocuments(folder.documents, nestedLevel + 1)}
            </React.Fragment>
          );
        })}
      </>
    );
  };

  const renderDocuments = (documents: Document[], nestedLevel = 0) => {
    return (
      <>
        {documents.map((document) => (
          <tr key={document.name} className="document-row border-b border-[#939BA4]">
            <td className="relative flex w-12 py-2 font-light">
              {nestedLevel === 0 ? (
                <div className="absolute flex h-7 w-7 items-center pl-7">
                  <FontAwesomeIcon icon={getIconByDocType(document.type)} className={`fa-fw absolute h-6 w-7`} />
                </div>
              ) : (
                <div
                  className="absolute flex h-7 w-7 items-center"
                  style={{ left: `${Math.min(nestedLevel * 28, 500)}px` }}
                >
                  <FontAwesomeIcon icon={getIconByDocType(document.type)} className={`fa-fw absolute h-6 w-7`} />
                </div>
              )}
            </td>
            <td className={`relative items-center whitespace-nowrap font-light text-primary  xl:-left-20`}>
              {nestedLevel === 0 ? (
                <span className="absolute top-3">{renderDocumentLink(document.name)}</span>
              ) : (
                <span
                  title={document.name}
                  className="absolute top-3"
                  style={{
                    left: `${Math.min((nestedLevel - 1) * 28, 500)}px`,
                  }}
                >
                  {renderDocumentLink(trimTitle(document.name))}
                </span>
              )}
            </td>
            <td className="py-2 pr-2 text-right font-light text-primary">
              {DocumentCategory[document.category as unknown as keyof typeof DocumentCategory]}
            </td>
            <td className="w-24 py-2 pl-5 text-right font-light text-primary">{formatFileSize(document.size)}</td>
            <td className="py-2 pr-2 text-right font-light text-primary">
              {document.parts && document.parts + ' ' + getFileContentTitle(document.type, document.parts)}
            </td>
          </tr>
        ))}
      </>
    );
  };

  return (
    documents.length > 0 && (
      <RoundedSectionCard>
        <div className="flex-col items-stretch justify-between gap-4 pb-4">
          <div className="flex w-full items-center justify-between">
            <div className="mb-5 flex gap-2">
              <span className="flex h-7 w-6 items-center justify-center">
                <FontAwesomeIcon className="fa-fw h-6 w-6" icon={faFolderOpen} />
              </span>
              <h2 className="text-2xl font-light">DOKUMENTE</h2>
            </div>
            {showAccessButton && (
              <div className="hidden md:block">
                <TenderAccessButton
                  tenderId={tenderId}
                  isAuthenticated={isAuthenticated}
                  hasAccess={hasAccessPermission}
                  label={'Zum Downloadportal'}
                />
              </div>
            )}
          </div>

          <div className="overflow-x-auto overflow-y-auto" style={{ maxHeight: '16rem' }}>
            <table className="w-full min-w-5xl pr-4">
              <thead className="sticky top-0 z-10 bg-base-200">
                <tr className="border-b border-[#939BA4]">
                  <th />
                  <th className="relative w-lg text-left text-xl font-light text-primary">
                    <span className="absolute top-0 pl-5">Dateiname</span>
                  </th>
                  <th className="w-48 pl-3 pr-2 text-right text-xl font-light text-primary">Kategorie</th>
                  <th className="pl-5 text-right text-xl font-light text-primary">Dateigröße</th>
                  <th className="w-28 pr-2 text-right text-xl font-light text-primary">Inhalt</th>
                </tr>
              </thead>
              <tbody>
                <>
                  {renderFoldersWithDocuments(rootFolders)}
                  {renderDocuments(rootDocuments)}
                </>
              </tbody>
            </table>
          </div>
          {showAccessButton && (
            <div className="mt-4 md:hidden">
              <TenderAccessButton
                tenderId={tenderId}
                isAuthenticated
                hasAccess={hasAccessPermission}
                label={'Zum Downloadportal'}
              />
            </div>
          )}
        </div>
      </RoundedSectionCard>
    )
  );
};

export default DocumentSection;
